import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useAuth from '../auth/feature/auth';
import { teamService } from './service/team.service';

export const JoinTeamPage = () => {
  const { t } = useTranslation();
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [needsPassword, setNeedsPassword] = React.useState<boolean | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const { data: teamInfo, isLoading: teamLoading } = useQuery({
    queryKey: ['team-invite', inviteCode],
    queryFn: () => teamService.getTeamByInviteCode(inviteCode!),
    enabled: !!inviteCode && isAuthenticated === true
  });

  const { mutate: join, isPending } = useMutation({
    mutationFn: (pw?: string) => teamService.joinTeam(inviteCode!, pw),
    onSuccess: (data) => {
      if (data) {
        navigate(`/team/${data.id}`);
      }
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message;
      if (msg === 'Incorrect team password') {
        toast.error(t('teamJoin.incorrectPassword'));
      } else {
        toast.error(msg || t('teamJoin.joinFailed'));
      }
    }
  });

  React.useEffect(() => {
    if (isAuthenticated === false) {
      navigate(
        `/auth/signup?redirect=${encodeURIComponent(`/team/join/${inviteCode}`)}`
      );
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    if (isAuthenticated && teamInfo) {
      const info = teamInfo as any;
      if (info.hasPassword) {
        setNeedsPassword(true);
      } else {
        setNeedsPassword(false);
        setShowConfirm(true);
      }
    }
  }, [isAuthenticated, teamInfo]);

  if (authLoading || teamLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1E] dark:border-[#C8C8CB] border-t-transparent rounded-full mb-[16px]" />
        <p className="font-body text-[16px] text-gray-900 dark:text-[#A0A0A3]">{t('teamJoin.joining')}</p>
      </div>
    );
  }

  // Confirmation modal for non-password teams
  if (showConfirm && !needsPassword) {
    const info = teamInfo as any;
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-[16px]">
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)] max-w-[400px] w-full mx-[16px] text-center">
          <div className="w-[48px] h-[48px] rounded-full bg-[#F5F5F6] dark:bg-[#28282B] flex items-center justify-center mx-auto mb-[16px]">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1D1D1E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h2 className="font-display text-[22px] font-bold text-base-black dark:text-white mb-[4px]">
            {info?.name || t('teamJoin.joinTeamFallback')}
          </h2>
          <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] mb-[20px]">
            {t('teamJoin.confirmJoinMessage', { name: info?.name || '', count: info?.memberCount || 0 })}
          </p>
          <div className="flex gap-[8px]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => { setShowConfirm(false); join(undefined); }}
              disabled={isPending}
              className="flex-1 h-[44px] rounded-[32px] bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB] transition-all disabled:opacity-50"
            >
              {isPending ? t('teamJoin.joining') : t('teamJoin.joinButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state while joining
  if (isPending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1E] dark:border-[#C8C8CB] border-t-transparent rounded-full mb-[16px]" />
        <p className="font-body text-[16px] text-gray-900 dark:text-[#A0A0A3]">{t('teamJoin.joining')}</p>
      </div>
    );
  }

  if (needsPassword) {
    const info = teamInfo as any;
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-[16px]">
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)] max-w-[400px] w-full mx-[16px]">
          <h2 className="font-display text-[22px] font-bold text-base-black dark:text-white mb-[4px] text-center">
            {info?.name ? t('teamJoin.joinTeam', { name: info.name }) : t('teamJoin.joinTeamFallback')}
          </h2>
          <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] mb-[16px] text-center">
            {t('teamJoin.passwordRequired')}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              join(password);
            }}
            className="flex flex-col gap-[8px]"
          >
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('teamJoin.passwordPlaceholder')}
                className="w-full h-[44px] px-[16px] pr-[44px] text-[16px] font-body text-base-black dark:text-white bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#BFBFC0] dark:focus:border-[#5DE1FF]/40 focus:shadow-[0_0_0_4px_rgba(29,29,30,0.12)] dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)] transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-[14px] top-1/2 -translate-y-1/2 text-[#818185] hover:text-base-black dark:hover:text-white transition-colors"
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <button
              type="submit"
              disabled={!password.trim()}
              className={`h-[44px] rounded-[32px] font-body text-[14px] font-medium transition-all ${
                !password.trim() ? 'bg-gray-900 opacity-50 text-white' : 'bg-base-black dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#B8B8BB]'
              }`}
            >
              {t('teamJoin.joinButton')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px]">
      <p className="font-body text-[16px] text-gray-900 dark:text-[#A0A0A3]">{t('teamJoin.joining')}</p>
    </div>
  );
};
